import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2,
  Trash2,
  Pencil,
  Plus,
  Star,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Toaster, toast } from "sonner";
import { useSessionState } from "@/hooks/useSessionState";
import { useSourcesList } from "@/hooks/useSources";
import {
  listFeaturedSources,
  createFeaturedSource,
  updateFeaturedSource,
  deleteFeaturedSource,
  TIER_LIMITS,
} from "@/services/featuredSourcesApi";
import type { FeaturedSource, SourceTier } from "@/services/featuredSourcesApi";
import { ApiError } from "@/services/api";
import type { FeaturedSourcesSessionState } from "@/types/pageState";

const featuredSourcesKeys = {
  all: ["featured-sources"] as const,
  list: () => [...featuredSourcesKeys.all, "list"] as const,
};

// Format an ISO date string to a readable local date, or return "—" if null.
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Convert a datetime-local input value (YYYY-MM-DDTHH:mm) to ISO 8601 with seconds.
function localToIso(value: string): string | null {
  if (!value) return null;
  return `${value}:00Z`;
}

// Convert an ISO 8601 string to datetime-local input value (YYYY-MM-DDTHH:mm).
function isoToLocal(iso: string | null): string {
  if (!iso) return "";
  return iso.slice(0, 16);
}

interface SponsorshipFormState {
  sourceId: string;
  tier: SourceTier;
  startDate: string;
  endDate: string;
}

const defaultForm: SponsorshipFormState = {
  sourceId: "",
  tier: "sponsored",
  startDate: "",
  endDate: "",
};

export default function FeaturedSources() {
  const queryClient = useQueryClient();

  const [sessionState, setSessionState] =
    useSessionState<FeaturedSourcesSessionState>({
      key: "featured-sources-ui",
      defaultValue: {
        addDialogOpen: false,
        editId: null,
        deleteId: null,
      },
    });

  const [form, setForm] = useState<SponsorshipFormState>(defaultForm);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);

  // Fetch all sponsorships
  const { data: sponsorships = [], isLoading } = useQuery({
    queryKey: featuredSourcesKeys.list(),
    queryFn: listFeaturedSources,
  });

  // Fetch all sources for the picker
  const { data: sourcesResponse } = useSourcesList(true);
  const allSources = useMemo(
    () => sourcesResponse?.sources ?? [],
    [sourcesResponse],
  );

  // Source ID → name lookup
  const sourceNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of allSources) {
      map[s.id] = s.name;
    }
    return map;
  }, [allSources]);

  // Sources not already sponsored
  const sponsoredSourceIds = useMemo(
    () => new Set(sponsorships.map((s) => s.source_id)),
    [sponsorships],
  );

  const availableSources = useMemo(
    () => allSources.filter((s) => !sponsoredSourceIds.has(s.id)),
    [allSources, sponsoredSourceIds],
  );

  // Tier counts
  const premiumCount = sponsorships.filter((s) => s.tier === "premium").length;
  const sponsoredCount = sponsorships.filter(
    (s) => s.tier === "sponsored",
  ).length;

  // Currently editing entry (for pre-filling the edit dialog)
  const editEntry = useMemo(
    () =>
      sessionState.editId != null
        ? (sponsorships.find((s) => s.id === sessionState.editId) ?? null)
        : null,
    [sessionState.editId, sponsorships],
  );

  const deleteEntry = useMemo(
    () =>
      sessionState.deleteId != null
        ? (sponsorships.find((s) => s.id === sessionState.deleteId) ?? null)
        : null,
    [sessionState.deleteId, sponsorships],
  );

  // Add mutation
  const addMutation = useMutation({
    mutationFn: () =>
      createFeaturedSource({
        source_id: form.sourceId,
        tier: form.tier,
        start_date: localToIso(form.startDate),
        end_date: localToIso(form.endDate),
      }),
    onSuccess: () => {
      toast.success(
        `"${sourceNameById[form.sourceId] ?? form.sourceId}" added to ${form.tier} tier.`,
      );
      queryClient.invalidateQueries({ queryKey: featuredSourcesKeys.all });
      setSessionState((prev) => ({ ...prev, addDialogOpen: false }));
      setForm(defaultForm);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.warning(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to add sponsorship.",
        );
      }
    },
  });

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: () =>
      updateFeaturedSource(sessionState.editId!, {
        tier: form.tier,
        start_date: localToIso(form.startDate),
        end_date: localToIso(form.endDate),
      }),
    onSuccess: () => {
      toast.success("Sponsorship updated.");
      queryClient.invalidateQueries({ queryKey: featuredSourcesKeys.all });
      setSessionState((prev) => ({ ...prev, editId: null }));
      setForm(defaultForm);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        toast.warning(err.message);
      } else {
        toast.error(
          err instanceof Error ? err.message : "Failed to update sponsorship.",
        );
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteFeaturedSource(id),
    onSuccess: (_, id) => {
      const entry = sponsorships.find((s) => s.id === id);
      toast.success(
        `"${sourceNameById[entry?.source_id ?? ""] ?? entry?.source_id}" removed from featured.`,
      );
      queryClient.invalidateQueries({ queryKey: featuredSourcesKeys.all });
      setSessionState((prev) => ({ ...prev, deleteId: null }));
    },
    onError: (err) => {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove sponsorship.",
      );
    },
  });

  function openAddDialog() {
    setForm(defaultForm);
    setSessionState((prev) => ({ ...prev, addDialogOpen: true }));
  }

  function openEditDialog(entry: FeaturedSource) {
    setForm({
      sourceId: entry.source_id,
      tier: entry.tier,
      startDate: isoToLocal(entry.start_date),
      endDate: isoToLocal(entry.end_date),
    });
    setSessionState((prev) => ({ ...prev, editId: entry.id }));
  }

  function openDeleteDialog(entry: FeaturedSource) {
    setSessionState((prev) => ({ ...prev, deleteId: entry.id }));
  }

  const premiumEntries = sponsorships.filter((s) => s.tier === "premium");
  const sponsoredEntries = sponsorships.filter((s) => s.tier === "sponsored");

  return (
    <>
      <Toaster position="top-right" theme="dark" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Featured Sources</h2>
          <Button onClick={openAddDialog} size="sm">
            <Plus className="size-4" />
            Add Sponsorship
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="size-5 animate-spin" />
            Loading sponsorships…
          </div>
        )}

        {!isLoading && (
          <div className="space-y-6">
            <TierCard
              tier="premium"
              entries={premiumEntries}
              count={premiumCount}
              limit={TIER_LIMITS.premium}
              sourceNameById={sourceNameById}
              onEdit={openEditDialog}
              onDelete={openDeleteDialog}
              deletePending={deleteMutation.isPending}
            />
            <TierCard
              tier="sponsored"
              entries={sponsoredEntries}
              count={sponsoredCount}
              limit={TIER_LIMITS.sponsored}
              sourceNameById={sourceNameById}
              onEdit={openEditDialog}
              onDelete={openDeleteDialog}
              deletePending={deleteMutation.isPending}
            />
          </div>
        )}
      </div>

      {/* Add dialog */}
      <Dialog
        open={sessionState.addDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, addDialogOpen: open }))
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sponsorship</DialogTitle>
            <DialogDescription>
              Assign a source to a featured tier. Each source can only hold one
              slot.
            </DialogDescription>
          </DialogHeader>
          <SponsorshipForm
            form={form}
            onChange={setForm}
            sources={availableSources}
            sourceNameById={sourceNameById}
            sourcePickerOpen={sourcePickerOpen}
            onSourcePickerOpenChange={setSourcePickerOpen}
            showSourcePicker
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, addDialogOpen: false }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={!form.sourceId || addMutation.isPending}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Add"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={sessionState.editId != null}
        onOpenChange={(open) => {
          if (!open) setSessionState((prev) => ({ ...prev, editId: null }));
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Sponsorship</DialogTitle>
            <DialogDescription>
              {editEntry
                ? `Editing ${sourceNameById[editEntry.source_id] ?? editEntry.source_id}`
                : "Edit sponsorship details."}
            </DialogDescription>
          </DialogHeader>
          <SponsorshipForm
            form={form}
            onChange={setForm}
            sources={[]}
            sourceNameById={sourceNameById}
            sourcePickerOpen={false}
            onSourcePickerOpenChange={() => {}}
            showSourcePicker={false}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, editId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              disabled={editMutation.isPending}
              onClick={() => editMutation.mutate()}
            >
              {editMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={sessionState.deleteId != null}
        onOpenChange={(open) => {
          if (!open) setSessionState((prev) => ({ ...prev, deleteId: null }));
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Sponsorship</DialogTitle>
            <DialogDescription>
              Remove{" "}
              <strong>
                {deleteEntry
                  ? (sourceNameById[deleteEntry.source_id] ??
                    deleteEntry.source_id)
                  : "this source"}
              </strong>{" "}
              from featured? It will drop to Tier 3 (standard) on the next
              directory request.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, deleteId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (sessionState.deleteId != null) {
                  deleteMutation.mutate(sessionState.deleteId);
                }
              }}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Remove"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Tier card ────────────────────────────────────────────────────────────────

interface TierCardProps {
  tier: SourceTier;
  entries: FeaturedSource[];
  count: number;
  limit: number;
  sourceNameById: Record<string, string>;
  onEdit: (entry: FeaturedSource) => void;
  onDelete: (entry: FeaturedSource) => void;
  deletePending: boolean;
}

function TierCard({
  tier,
  entries,
  count,
  limit,
  sourceNameById,
  onEdit,
  onDelete,
  deletePending,
}: TierCardProps) {
  const atLimit = count >= limit;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {tier === "premium" ? (
              <Star className="size-4 text-yellow-500" />
            ) : (
              <Star className="size-4 text-blue-400" />
            )}
            <span className="capitalize">{tier}</span>
          </div>
          <Badge
            variant={atLimit ? "destructive" : "secondary"}
            className="text-xs font-normal"
          >
            {count} / {limit}
          </Badge>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {tier === "premium"
            ? "Top of directory, shuffled. Max 5 slots."
            : "Below premium, shuffled. Max 20 slots."}
        </p>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No {tier} sponsorships yet.
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">
                    {sourceNameById[entry.source_id] ?? entry.source_id}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {entry.source_id}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground text-right shrink-0 space-y-0.5">
                  <div>
                    {entry.start_date || entry.end_date ? (
                      <>
                        {formatDate(entry.start_date)} →{" "}
                        {formatDate(entry.end_date)}
                      </>
                    ) : (
                      <span className="text-muted-foreground/50">
                        No date range
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => onEdit(entry)}
                  title="Edit sponsorship"
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deletePending}
                  onClick={() => onDelete(entry)}
                  title="Remove sponsorship"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Sponsorship form (shared between add and edit dialogs) ───────────────────

interface SourceOption {
  id: string;
  name: string;
}

interface SponsorshipFormProps {
  form: SponsorshipFormState;
  onChange: (form: SponsorshipFormState) => void;
  sources: SourceOption[];
  sourceNameById: Record<string, string>;
  sourcePickerOpen: boolean;
  onSourcePickerOpenChange: (open: boolean) => void;
  showSourcePicker: boolean;
}

function SponsorshipForm({
  form,
  onChange,
  sources,
  sourceNameById,
  sourcePickerOpen,
  onSourcePickerOpenChange,
  showSourcePicker,
}: SponsorshipFormProps) {
  return (
    <div className="space-y-4 py-2">
      {showSourcePicker && (
        <div className="space-y-2">
          <Label>Source</Label>
          <Popover
            open={sourcePickerOpen}
            onOpenChange={onSourcePickerOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={sourcePickerOpen}
                className="w-full justify-between font-normal"
              >
                {form.sourceId
                  ? (sourceNameById[form.sourceId] ?? form.sourceId)
                  : "Select a source…"}
                <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command>
                <CommandInput placeholder="Search sources…" />
                <CommandList>
                  <CommandEmpty>No sources found.</CommandEmpty>
                  <CommandGroup>
                    {sources.map((s) => (
                      <CommandItem
                        key={s.id}
                        value={`${s.name} ${s.id}`}
                        onSelect={() => {
                          onChange({ ...form, sourceId: s.id });
                          onSourcePickerOpenChange(false);
                        }}
                      >
                        <Check
                          className={`mr-2 size-4 ${form.sourceId === s.id ? "opacity-100" : "opacity-0"}`}
                        />
                        <span>{s.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground font-mono">
                          {s.id}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="sponsorship-tier">Tier</Label>
        <Select
          value={form.tier}
          onValueChange={(v) => onChange({ ...form, tier: v as SourceTier })}
        >
          <SelectTrigger id="sponsorship-tier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="premium">
              Premium — top of directory, max 5
            </SelectItem>
            <SelectItem value="sponsored">
              Sponsored — below premium, max 20
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="sponsorship-start">Start date</Label>
          <Input
            id="sponsorship-start"
            type="datetime-local"
            value={form.startDate}
            onChange={(e) => onChange({ ...form, startDate: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sponsorship-end">End date</Label>
          <Input
            id="sponsorship-end"
            type="datetime-local"
            value={form.endDate}
            onChange={(e) => onChange({ ...form, endDate: e.target.value })}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Leave dates empty for an open-ended sponsorship.
      </p>
    </div>
  );
}
